import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { Backend } from 'src/app/backend-interface/backend';
import { BwFilters, BwObjectPromise } from 'src/app/service/global.service';
import { TranslationService } from 'src/app/service/translation.service';
import { NgbDropdownModule, NgbCollapseModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { MatButtonModule } from '@angular/material/button';


/**
 * Child Component for ListeAttestation && ListeCompromiseReport
 * 
 * It's the component for filters search of attestations && compromise reports
 * 
 * All categories of filters have his own FormGroup: Application, Os, Model, B&W versions and Ids selection
 * 
 * Os and Applications have also formgroup for their own versions.
 * 
 * It's build for correspond at checkboxs witch the name of choice at key and true/false as value
 */
@Component({
  selector: 'app-filters-ac',
  templateUrl: './filters-ac.component.html',
  styleUrls: ['./filters-ac.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatChipsModule,
    TranslateModule,
    NgbDropdownModule,
    NgbCollapseModule,
    NgbTooltipModule,
    MatButtonModule,
],
  providers: [TranslationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class FiltersACComponent implements OnChanges {

  @Input() objetAC?: BwObjectPromise
  losactif = signal<string[]>([]);
  @Output() applyFilter = new EventEmitter<BwFilters>();
  @Input() defaultFilter?: BwFilters

  readonly formOS = this.formBuilder.group({
    android: false,
    ios: false,
    ipados: false,
    harmony: false,
  })

  public readonly formVOS = this.formBuilder.group({
    android: this.formBuilder.group({}),
    ios: this.formBuilder.group({}),
    ipados: this.formBuilder.group({}),
    harmony: this.formBuilder.group({}),
  })

  readonly formModel = this.formBuilder.group({});

  readonly formSearch = this.formBuilder.group({
    id: false,
    deviceId: false,
    bwAppId: false,
  })

  readonly formBW = this.formBuilder.group({});
  readonly formApplication = this.formBuilder.group({});
  readonly formVersionApplication = this.formBuilder.group({});
  osselected = signal<boolean>(false);

  selectedApp = signal<string>('Application');
  selectedModele = signal<string>('');
  selectedBWV = signal<string>('');
  selectedOS = signal<string>('OS');
  searchmodel = new FormControl<string>('');

  lapp = this.getAllAppNames()

  readonly translationService = inject(TranslationService);


  constructor(private readonly formBuilder: UntypedFormBuilder) {
   
    this.translationService.get('filter-model').subscribe((val: string) => {
      this.selectedModele.set(val ?? 'Modèle');
    });
    this.translationService.get('filtre-bw').subscribe((val: string) => {
      this.selectedBWV.set(val ?? 'BW');
    });
  }

  /**
   * Changes going to represent the objectPromise given by parent component.
   * 
   * If this item change it will recreate all forms.
   * 
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.isCollapsed.set(false);
    if (changes && this.objetAC?.results) {
      Backend.FILTRES.initFormWithSubForm(this.formApplication, this.formVersionApplication, this.getAllAppNames())
      this.initFiltreBWV()
      this.initModelsForm()
      this.initMetadataForm()
      this.resetOSFiltre()
      if (this.defaultFilter) {
        this.defaultPatchFilters()
      }
    }
  }

  /**
   * Initialize all default forms
   */
  defaultPatchFilters() {
    this.defaultPatchAppFilter()
    this.defaultPatchBW()
    this.defaultPatchOS()
    this.defaultPatchModel()
  }
  private patchFormGroupValues(form: FormGroup, values: string[], value: boolean = true): void {
    if (values && values.length > 0) {
      const patch: Record<string, boolean> = {};
      for (const v of values) patch[v] = value;
      form.patchValue(patch);
    }
  }

  defaultPatchModel(): void {
    this.patchFormGroupValues(this.formModel, this.defaultFilter?.models ?? []);
  }

  defaultPatchOS() {
    if (!this.defaultFilter?.platformNames || this.defaultFilter.platformNames.length === 0) {
      return;
    }
    this.defaultFilter.platformNames.forEach(os => {
      this.formOS.patchValue({ [os]: true });
      this.InitFormVOS(this.getAllVofOs(os), os);
      this.selectOS(os);
    });

    this.patchPlatformVersions();
  }

  private patchPlatformVersions() {
    const platforms = [
      { key: 'android', versions: this.defaultFilter?.androidPlatformVersions },
      { key: 'ios', versions: this.defaultFilter?.iosPlatformVersions }
    ];

    platforms.forEach(platform => {
      if (platform.versions) {
        const formGroup = this.formVOS.get(platform.key) as FormGroup;
        Object.keys(formGroup.controls).forEach(v => {
          if (platform.versions) {
            formGroup.patchValue({ [v]: platform.versions.includes(v) });
          }
        });
      }
    });
  }


  defaultPatchBW(): void {
    this.patchFormGroupValues(this.formBW, this.defaultFilter?.bwSdkVersions ?? []);
  }

  defaultPatchAppFilter() {
    if (this.defaultFilter?.appNamesAndVersions) {
      for (let app of Object.keys(this.defaultFilter?.appNamesAndVersions)) {
        this.formApplication.patchValue({ [app]: true })
        this.initVappForm(app)
        let formAppV = this.formVersionApplication.controls[app] as FormGroup
        for (let v of Object.keys(formAppV.controls)) {
          if (!this.defaultFilter?.appNamesAndVersions[app].includes(v)) {
            formAppV.patchValue({ [v]: false })
          }
        }
      }
    }
  }

  getLKeysSearch() {
    return Object.keys(this.formSearch.controls)
  }

  /** patch selected categorie for input search (id, bwid, deviceid) */
  changeSelected(e: any, cat: string) {
    for (let c of this.getLKeysSearch()) {
      if (c === cat) {
        this.formSearch.patchValue({ [c]: e.selected })
      } else { this.formSearch.patchValue({ [c]: false }) }
    }
  }
  getAllAppNames(): string[] {
    return Backend.BW_FILTRES.getAllAppNames(this.objetAC)
  }

  getVBW() {
    return Backend.BW_FILTRES.getAllBWVersions(this.objetAC)
  }

  getBWVTrue() {
    return Backend.FILTRES.getAllFieldTrue(this.formBW)
  }

  initFiltreBWV() {
    Backend.FILTRES.initSimpleForm(this.getVBW(), this.formBW)
  }

  formatLongString(chaine: string | undefined, n: number) {
    return Backend.FORMAT.formatN(chaine, n)
  }

  getAllVersionForApp(app: string) {
    return Backend.BW_FILTRES.getAllAppVersion(this.objetAC, app)
  }

  getAllOs() {
    return Backend.BW_FILTRES.getAllOS(this.objetAC)
  }

  /**
   * If all versions are check for an app in formVersionApplication it will check value app in the formApplication 
   * @param app 
   */
  updateAllCompleteApp(app: string) {
    let all = Backend.FILTRES.updateAllComplete(this.formVersionApplication, app)
    if (all) {
      this.formApplication.get(app)?.setValue(true);
    }
  }

  /**
   * If all versions are uncheck for an app in formVersionApplication it will uncheck value app in the formApplication 
   * @param app 
   */
  checkIfnoVforApp(app: string) {
    let no = Backend.FILTRES.checkIfnoValueTrue(this.formVersionApplication, app)
    if (no) {
      this.formApplication.get(app)?.setValue(false);
      Backend.FILTRES.deleteFormVersion(this.formVersionApplication, app)
    }
  }

  initVappForm(app: string) {
    if (this.formApplication.get(app)?.value) {
      const lVersionsApp = this.getAllVersionForApp(app)
      Backend.FILTRES.initFormVersionsForValue(this.formVersionApplication, lVersionsApp, app)
    }
  }

  /**
   * Verify if formVersionApplication have SOME check value ; if it is transform 
   * 
   * the checkbox's logo for the value app in the formApplication (see angular material doc)
   * 
   * @param app 
   * @returns boolean
   */
  someCompleteApp(app: string) {
    return Backend.FILTRES.someComplete(this.formVersionApplication, app)
  }

  getAllAppTrue() {
    return Backend.FILTRES.getAllFieldTrue(this.formApplication)
  }

  getAllVersionAppTrue() {
    let map: Record<string, any> = {}
    for (let app of this.getAllAppTrue()) {
      map[app] = Backend.FILTRES.getAllFieldTrueForSpecificValue(this.formVersionApplication, app)
    }
    return map
  }

  resetFormBWV() {
    Backend.FILTRES.resetSimpleForm(this.formBW)
  }

  formatVersionFiltre(version: string) {
    return Backend.FORMAT.formatVersionCheckBox(version)
  }

  getAllBwVforTF(): string[] {
    let l: any = []
    if (this.getBWVTrue().length > 0 && this.getBWVTrue().length < this.getVBW().length) {
      for (let bwv of this.getBWVTrue()) {
        l.push(this.formatLongString(bwv, 12))
      }
    }
    return l
  }

  formatAPPName(appName: string) {
    return Backend.FORMAT.formatAPPName(appName)
  }

  getAllVofOs(value: string | null) {
    let los: string[] = []
    if (value) {
      los = Backend.BW_FILTRES.getVersionsOS(this.objetAC, value)
    }
    return los
  }

  getAllVTrueOSforNamedOS(os: string) {
    if (this.someCompleteOS(os)) {
      return Backend.FILTRES.getAllFieldTrueForSpecificValue(this.formVOS, os.toLowerCase())
    } else return undefined
  }

  InitFormVOS(l: string[], os: string) {
    if (this.formOS.get(os.toLowerCase())?.value) {
      Backend.FILTRES.initFormVOS(this.formVOS, l, os)
      this.osselected.set(true);
    }
  }

  deleteFormVersionOS(os: string) {
    Backend.FILTRES.deleteFormVersion(this.formVOS, os.toLowerCase())
  }


  /**
    * Verify if formVOS have SOME check value ; if it is transform 
    * 
    * the checkbox's logo for the value os in the formOS (see angular material doc)
    * 
    * @param os 
    * @returns boolean
    */
  someCompleteOS(os: string) {
    return Backend.FILTRES.someComplete(this.formVOS, os.toLowerCase())
  }

  /**
   * If all versions are check for an os in formVOS it will check value app in the formOS 
   * @param os 
   */
  updateAllComplete(os: string) {
    let all = Backend.FILTRES.updateAllComplete(this.formVOS, os.toLowerCase())
    if (all) {
      this.formOS.get(os.toLowerCase())?.setValue(true);
    }
  }

  /**
   * If all versions are uncheck for an app in formVOS it will uncheck value app in the formOS 
   * @param os 
   */
  checkIfnoVforOS(os: string) {
    let no = Backend.FILTRES.checkIfnoValueTrue(this.formVOS, os.toLowerCase())
    if (no) {
      this.formOS.get(os.toLowerCase())?.setValue(false);
      this.selectOS(os)
    }
  }


  /**
   * Just a fonction for a better display in the button for the os
   * 
   * it will just add or delete os if they are check or uncheck
   * 
   * @param os
   */
  selectOS(os: string) {
    if (os === 'None') {
      this.selectedOS.set('OS');
      return;
    }

    const osLowerCase = os.toLowerCase();
    const osIsActive = this.formOS.get(osLowerCase)?.value;

    if (osIsActive) {
      this.handleActiveOS(osLowerCase, os);
    } else {
      this.handleInactiveOS(osLowerCase);
    }
  }

  private handleActiveOS(osLowerCase: string, os: string) {
    const los = this.losactif();
    if (!los.includes(osLowerCase)) {
      this.losactif.set([...los, osLowerCase]);
    }
    this.selectedOS.set(this.losactif().length > 1 ? 'icons' : os);
  }

  private handleInactiveOS(osLowerCase: string) {
    this.deleteFormVersionOS(osLowerCase);
    const los = this.losactif();
    const index = los.indexOf(osLowerCase);
    if (index !== -1) {
      const newLos = [...los];
      newLos.splice(index, 1);
      this.losactif.set(newLos);
    }
    this.updateSelectedOSAfterRemoval();
  }

  private updateSelectedOSAfterRemoval() {
    if (this.losactif().length === 1) {
      this.selectedOS.set(this.losactif()[0]);
    } else if (this.losactif().length === 0) {
      this.selectedOS.set("OS");
    }
  }

  /**
   * It will check if the form for search an id is valid
   * 
   * @returns boolean
   */
  checkSearch() {
    let checked = false
    let keys = Object.keys(this.formSearch.controls)
    for (let k of keys) {
      if (this.formSearch.get(k)?.value === true) {
        checked = true
      }
    }
    return (checked && (this.searchmodel.value ?? "").trim() !== "") || (this.searchmodel.value ?? "") === "";
  }

  getAllDevices(): string[] {
    return Backend.BW_FILTRES.getAllDevices(this.objetAC)
  }

  initModelsForm() {
    Backend.FILTRES.initSimpleForm(this.getAllDevices(), this.formModel)
  }

  initMetadataForm() {
    Backend.FILTRES.initMetadataSearchForm(Backend.ATTESTATIONS.isAttestation(this.objetAC), this.formSearch)
  }

  resetModelForm() {
    Backend.FILTRES.resetSimpleForm(this.formModel)
  }

  getAllModelesVforTF(): string[] {
    let l: any = []
    if (Backend.FILTRES.getAllFieldTrue(this.formModel).length > 0 && Backend.FILTRES.getAllFieldTrue(this.formModel).length < this.getAllDevices().length) {
      for (let models of Backend.FILTRES.getAllFieldTrue(this.formModel)) {
        l.push(models)
      }
    }
    return l
  }

  resetOSFiltre() {
    this.selectedOS.set("OS");
    this.losactif.set([]);
    this.formOS.reset()
    const os = Object.keys(this.formVOS.value)
    os.forEach((o) => {
      this.deleteFormVersionOS(o)
    })
  }

  resetApp() {
    Backend.FILTRES.resetFormAndSubForm(this.formApplication, this.formVersionApplication)
  }

  getModels() {
    return Backend.FILTRES.getAllFieldTrue(this.formModel)
  }

  getOStrue() {
    return Backend.FILTRES.getAllFieldTrue(this.formOS)
  }

  initSearchTrue(type: string) {
    if (this.formSearch.get(type)?.value) {
      return this.searchmodel.value ?? undefined;
    } else {
      return undefined;
    }
  }

  public isCollapsed = signal<boolean>(false);

  /**
   * It will construct an object Filter with all value user will selected
   * 
   * and going to send this item to the parent composent for activated the request.
   */
  applyFilters() {
    const objectFilter: BwFilters = {
      bwSdkVersions: this.getBWVTrue(),
      appNamesAndVersions: this.getAllVersionAppTrue(),
      platformNames: this.getOStrue(),
      models: this.getModels(),
      androidPlatformVersions: this.getAllVTrueOSforNamedOS('Android'),
      iosPlatformVersions: this.getAllVTrueOSforNamedOS('iOS'),
      id: this.initSearchTrue('id'),
      bwAppId: this.initSearchTrue('bwAppId'),
      deviceId: this.initSearchTrue('deviceId'),
      metadata: this.initSearchTrue('metadata'),
    };
    this.applyFilter.emit(objectFilter);
  }

  statusMoreFilters() {
    return this.isCollapsed() ? 'remove' : 'add';
  }
}
